<template>
    <div v-if="model && form">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="2">
                        <rkm-form-date-picker
                            label="Data inicial"
                            v-model="model.dataInicial"
                            :validations="validations.dataInicial"
                        />
                    </rkm-col>
                    <rkm-col cols="2">
                        <rkm-form-date-picker
                            label="Data final"
                            v-model="model.dataFinal"
                            :validations="validations.dataFinal"
                        />
                    </rkm-col>
                    <rkm-col cols="3">
                        <rkm-form-select
                            label="Município"
                            :options="form.municipios"
                            value-field="id"
                            text-field="nome"
                            v-model="model.municipio"
                            default-option-text="Todos"
                            show-default-option
                            :key="loaded"
                        />
                    </rkm-col>
                    <rkm-col>
                        <rkm-form-input
                            label="Protocolo"
                            v-model="model.protocolo"
                        />
                    </rkm-col>
                    <rkm-col>
                        <rkm-button
                            :label="false"
                            color="primary"
                            @click="search"
                        >Buscar</rkm-button>
                        <rkm-button
                            :label="false"
                            @click="reset"
                        >Limpar</rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>


        <rkm-area
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    cols="1"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        @change="selectAll"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedCols"
                        value-field="key"
                    />
                </rkm-dropdown>

                <rkm-button
                    :disabled="items.length === 0"
                    @click="print"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>

                <rkm-button
                    :disabled="items.length === 0"
                    @click="exportTo"
                    color="info"
                    small
                    title="Exportar"
                >
                    <rkm-icon icon="file-export" />
                </rkm-button>
            </template>

            <rkm-table
                :fields="fields"
                :items="items"
                sort-by="data"
                :sort-compare="sortCompare"
            >
                <template v-slot:cell(actions)="{ item }">
                    <rkm-button
                        color="primary"
                        small
                        title="Visualizar"
                        @click="$refs.Modal.open(item.id)"
                    >
                        <rkm-icon icon="search" />
                    </rkm-button>
                </template>

                <template #afterPagination>
                    <div class="mt-2">
                        <rkm-row>
                            <rkm-col>
                                <p>Quantidade = paciente + acompanhantes</p>
                            </rkm-col>
                        </rkm-row>
                    </div>
                </template>
            </rkm-table>
        </rkm-area>

        <rkm-area
            title="Total"
            no-margin-top
            v-if="items && items.length > 0"
            class="mt-2"
        >
            <rkm-row>
                <rkm-col cols="3">
                   Quantidade
                    <rkm-alert color="secondary">
                        <strong>
                            {{  sumPassageiros }}
                        </strong>
                    </rkm-alert>
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <Modal
            ref="Modal"
        />
    </div>
</template>

<script>
import { mapActions } from 'vuex'
import { repository, error, data, object, stringCase, file } from '@/mixins'
import moment from 'moment'
import Modal from './Modal'

export default {
    components: {
        Modal
    },
    computed: {
        validations() {
            return {
                dataInicial: {
                    required: true
                },
                dataFinal: {
                    required: true
                }
            }
        },
        optionalColumns() {
            return this.columns.filter(c => !(c.fixed || false))
        },
        fields() {
            return this.columns.filter(c => (c.fixed || false) || this.selectedCols.includes(c.key))
        },
        selectedAll() {
            return this.selectedCols.length == 3
        },
        sumPassageiros() {
            return this.items.reduce((acc,cur) => {
                return acc + cur.pacientes + cur.acompanhantes
            }, 0)
        }
    },
    created() {
        this.repositories = {
            relatorioViagem: this.getConectaSusRepository('transportePaciente.relatorioViagem'),
            impressao: this.getConectaSusRepository('impressao.transportePaciente'),
            exportacao: this.getConectaSusRepository('exportacao.transportePaciente'),
        }
        this.load()
    },
    data() {
        return {
            loaded: false,
            model: null,
            form: null,
            items: [],
            selectedCols: [
                'quantidade',
                'veiculo',
                'motorista'
            ],
            indeterminate: true,
            columns: [
                {
                    key: 'data',
                    label: 'Data',
                    sortable: true,
                    fixed: true,
                },
                {
                    key: 'horario',
                    label: 'Hora de saída',
                    sortable: true,
                    fixed: true,
                },
                {
                    key: 'municipio',
                    label: 'Município de destino',
                    sortable: true,
                    fixed: true,
                },
                {
                    key: 'quantidade',
                    label: 'Quantidade',
                    sortable: true,
                },
                {
                    key: 'veiculo',
                    label: 'Veículo',
                    sortable: true,
                },
                {
                    key: 'motorista',
                    label: 'Motorista',
                    sortable: true,
                },
                {
                    key: 'protocolo',
                    label: 'Protocolo',
                    fixed: true
                },
                {
                    key: 'actions',
                    label: 'Ações',
                    class: 'col-min',
                    fixed: true
                }
            ],
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        sortCompare(a, b, key) {
            if(key == 'data') {
                const dateA = moment(a[key], 'DD/MM/YYYY HH:mm')
                const dateB = moment(b[key], 'DD/MM/YYYY HH:mm')
                if (dateA > dateB) return 1
                else if (dateA < dateB) return -1
                else return 0
            } else {
                return false
            }
        },
        load() {
            this.setLoading()
            this.repositories.relatorioViagem.index()
                .then(({data}) => {
                    this.model = data.model
                    this.form = data.form
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getMunicipios() {
            this.setLoading()
            this.repositories.relatorioViagem.getMunicipios({
                'data_inicial': moment(this.model.dataInicial).format('Y-M-D'),
                'data_final': moment(this.model.dataFinal).format('Y-M-D'),
            })
                .then(({ data }) => {
                    this.form.municipios = data
                    this.loaded = !this.loaded
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        search() {
            this.$refs.Form.validate().then(valid => {
                if(!valid) return
                this.setLoading()
                this.repositories.relatorioViagem.list({
                    'data_inicial': moment(this.model.dataInicial).format('Y-M-D'),
                    'data_final': moment(this.model.dataFinal).format('Y-M-D'),
                    'municipio_id': this.model.municipio,
                    'protocolo': this.model.protocolo
                })
                    .then(({data}) => {
                        this.items = data
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            })
        },
        reset() {
            this.model.dataInicial = null
            this.model.dataFinal = null
            this.items = []
        },
        print() {
            this.setLoading()
            this.repositories.impressao.relatorioViagem(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        exportTo() {
            this.setLoading()
            this.repositories.exportacao.relatorioViagem(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        prepareDataWithColumns() {
            const data = this.prepareData()
            data.selectedCols = this.fields.map(f => {
                return {
                    key: this.camelToSnake(f.key),
                    label: f.label,
                    class: f.class
                }
            })
            return data
        },
        prepareData() {
            const data = this.clone(this.model)
            data.dataInicial = moment(data.dataInicial).format('Y-M-D')
            data.dataFinal = moment(data.dataFinal).format('Y-M-D')
            return this.camelObjectKeysToSnakeRecursive(data)
        },
        selectAll(selected) {
            this.selectedCols = selected ? this.columns.map(c => c.key) : []
        }
    },
    watch: {
        'model.dataInicial': function() {
            if(this.model.dataInicial && this.model.dataFinal) {
                this.getMunicipios()
            }
        },
        'model.dataFinal': function() {
            if(this.model.dataInicial && this.model.dataFinal) {
                this.getMunicipios()
            }
        }
    },
    mixins: [repository, error, data, object, stringCase, file],
}
</script>